import type { LinkProps } from "@tanstack/react-router";
import type {
  NotificationType,
  NotifyInput,
} from "@/app/providers/NotificationProvider";
import type { AlertDTO, AlertRule, AlertSeverity } from "../../domain/types";

const SEVERITY_RANK: Record<AlertSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

type RuleGroupConfig = {
  title: string;
  pluralDescription: (count: number) => string;
  defaultTo?: string;
};

const RULE_GROUP: Record<AlertRule, RuleGroupConfig> = {
  low_stock: {
    title: "ສະຕັອກໃກ້ໝົດ",
    pluralDescription: (n) => `${n} ຕົວເລືອກຕ່ຳກວ່າເກນ`,
    defaultTo: "/app/products",
  },
  out_of_stock: {
    title: "ສະຕັອກຫມົດ",
    pluralDescription: (n) => `${n} ຕົວເລືອກສະຕັອກຫມົດ`,
    defaultTo: "/app/products",
  },
  order_stale_paid: {
    title: "ລໍຖ້າແພັກ",
    pluralDescription: (n) => `${n} ອໍເດີ້ລໍຖ້າແພັກ`,
    defaultTo: "/app/fulfillment/pack",
  },
  order_stale_shipped_cod: {
    title: "COD ຄ້າງຮັບ",
    pluralDescription: (n) => `${n} ອໍເດີ້ COD ຄ້າງຮັບເງິນ`,
    defaultTo: "/app/fulfillment",
  },
  import_arriving: {
    title: "ຂອງໃກ້ຮອດ",
    pluralDescription: (n) => `${n} ໃບສັ່ງນຳເຂົ້າໃກ້ຮອດ`,
    defaultTo: "/app/import-orders",
  },
  import_overdue: {
    title: "ຂອງເກີນກຳນົດ",
    pluralDescription: (n) => `${n} ໃບສັ່ງນຳເຂົ້າເກີນກຳນົດ`,
    defaultTo: "/app/import-orders",
  },
  import_at_carrier_stale: {
    title: "ຢູ່ຂົນສົ່ງນาน",
    pluralDescription: (n) => `${n} ໃບຄ້າງຢູ່ຂົນສົ່ງ`,
    defaultTo: "/app/import-orders",
  },
  import_pending_lines: {
    title: "ລາຍການລໍຖ້າຈັບຄູ່",
    pluralDescription: (n) => `${n} ໃບສັ່ງມີ pending ເກີນເວລາ`,
    defaultTo: "/app/import-orders",
  },
  reorder_critical: {
    title: "ຄວນສັ່ງເພີ່มດ່ວນ",
    pluralDescription: (n) => `${n} ລາຍການສະຕັອກຫມົດ — ຄວນສັ່ງເພີ່ม`,
    defaultTo: "/app/inventory",
  },
  aging_stock: {
    title: "lot ຄ້າງສະຕັອກ",
    pluralDescription: (n) => `${n} lot ອາຍຸຍາວ — ຄວນກວດສອບ`,
    defaultTo: "/app/reports?tab=stock&section=aging",
  },
  gr_draft_pending: {
    title: "ໃບຮັບເຂົ້າຮ່າງຄ້າງຢືນຢັນ",
    pluralDescription: (n) => `${n} ໃບຮັບເຂົ້າຮ່າງຄ້າງຢືນຢັນ`,
    defaultTo: "/app/inventory?status=draft",
  },
  restock_waitlist_ready: {
    title: "ລາຍການພ້ອມແຈ້ງ",
    pluralDescription: (n) => `${n} ລາຍການລໍຖ້າສິນຄ້າພ້ອມແຈ້ງ`,
    defaultTo: "/app/restock-waitlist?status=ready",
  },
};

function mapSeverity(severity: AlertSeverity): NotificationType {
  if (severity === "error") return "error";
  if (severity === "warning") return "warning";
  return "info";
}

function worstSeverity(alerts: AlertDTO[]): AlertSeverity {
  return alerts.reduce<AlertSeverity>(
    (worst, alert) =>
      SEVERITY_RANK[alert.severity] < SEVERITY_RANK[worst]
        ? alert.severity
        : worst,
    "info",
  );
}

export type GroupedAlertNotification = {
  notify: NotifyInput;
  alertIds: string[];
};

/** รวม alerts ตาม rule เป็นหนึ่งการแจ้งเตือนต่อประเภท */
export function groupAlertsForNotify(
  alerts: AlertDTO[],
): GroupedAlertNotification[] {
  const byRule = new Map<AlertRule, AlertDTO[]>();
  for (const alert of alerts) {
    const list = byRule.get(alert.rule) ?? [];
    list.push(alert);
    byRule.set(alert.rule, list);
  }

  const result: GroupedAlertNotification[] = [];

  for (const [rule, group] of byRule) {
    const alertIds = group.map((a) => a.id);
    const batchId = alertIds.slice().sort().join("|");

    if (group.length === 1) {
      const alert = group[0];
      result.push({
        alertIds,
        notify: {
          id: `operational-group:${rule}:${alert.id}`,
          tag: `operational:${rule}`,
          title: alert.title,
          description: alert.description,
          type: mapSeverity(alert.severity),
          to: alert.to as LinkProps["to"],
        },
      });
      continue;
    }

    const config = RULE_GROUP[rule];
    result.push({
      alertIds,
      notify: {
        id: `operational-group:${rule}:${batchId}`,
        tag: `operational:${rule}`,
        title: config.title,
        description: config.pluralDescription(group.length),
        type: mapSeverity(worstSeverity(group)),
        to: (group.find((a) => a.to)?.to ?? config.defaultTo) as LinkProps["to"],
      },
    });
  }

  return result;
}
